import { useState } from "react";
import { BellOff } from "lucide-react";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import type * as API from "@shared-core/data/api-types";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import { currentTime } from "@shared-core/domain/current-time";

interface TicketAlertMuteControlProps {
  /** Active suppression, if this ticket is currently muted. */
  alertSuppression: API.AlertSuppressionSummary | null;
  /** Mute every alert definition currently backing the ticket until
   * expiresAt (an ISO datetime), with reason. Rethrows on failure so the
   * dialog can show the error inline and stay open. */
  onMute: (reason: string, expiresAt: string) => Promise<void>;
  /** Clear an active mute before it expires. */
  onClear: () => Promise<void>;
}

/** "No need to watch" ticket action (customer-alert-suppression feature).
 *
 * Renders either a trigger button (opens a reason + expiry dialog) or, once
 * muted, a banner naming the expiry and reason with a Clear affordance —
 * same dialog-form + inline-banner shape as `AccountHealthCard`'s manual
 * health-tier override on the customer page.
 */
export function TicketAlertMuteControl({
  alertSuppression,
  onMute,
  onClear,
}: TicketAlertMuteControlProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      setFormError("A reason is required.");
      return;
    }
    if (!expiresAt) {
      setFormError("An expiry date is required.");
      return;
    }
    const expiresAtIso = new Date(`${expiresAt}T23:59:59`).toISOString();
    if (new Date(expiresAtIso) <= currentTime()) {
      setFormError("Expiry must be in the future.");
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      await onMute(reason.trim(), expiresAtIso);
      setReason("");
      setExpiresAt("");
      setDialogOpen(false);
    } catch (err) {
      console.error("[ticket alert mute] set failed:", err);
      setFormError("Couldn't save the mute — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await onClear();
    } catch (err) {
      console.error("[ticket alert mute] clear failed:", err);
      setFormError("Couldn't clear the mute — please try again.");
    } finally {
      setClearing(false);
    }
  };

  if (alertSuppression) {
    return (
      <div className="mb-3 flex items-start gap-2 rounded-md border border-muted-foreground/20 bg-muted/50 px-3 py-2 text-sm">
        <BellOff className="size-4 shrink-0 mt-0.5 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p>
            <span className="font-medium">
              Muted until {formatDateOrDash(alertSuppression.expires_at)}
            </span>
            : {alertSuppression.reason}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClear}
          disabled={clearing}
        >
          {clearing ? "Clearing…" : "Clear"}
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mb-3"
        onClick={() => setDialogOpen(true)}
      >
        <BellOff className="size-3.5" />
        No need to watch
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>No need to watch</DialogTitle>
            <DialogDescription>
              Mute the alert type(s) behind this ticket for this customer until
              a chosen date. A fresh alert of the same type won&apos;t raise a
              new ticket while muted.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-2.5">
            <div>
              <label className="block text-xs mb-1 text-muted-foreground">
                Mute until
              </label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs mb-1 text-muted-foreground">
                Reason
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. customer confirmed this is a known, expected seasonal dip"
                rows={3}
                className="text-sm"
              />
            </div>
            {formError && (
              <p className="text-xs text-destructive">{formError}</p>
            )}
            <DialogFooter>
              <Button type="submit" size="sm" disabled={submitting}>
                {submitting ? "Saving…" : "Mute"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
